import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../utils/apiUtils';
import { Container, Card, Row, Col, Badge, Button, Alert, Table, Modal, Form } from 'react-bootstrap';
import { useNotification } from '../hooks/useNotification';
import { getItemPermissions, canToggleEditLock } from '../utils/permissionUtils';
import MonitoringToolModal from './MonitoringToolModal';

// Helper function to format proponents - removes task and keeps only honorific and name
const formatProponents = (proponentsString) => {
  if (!proponentsString) return '';
  
  // If the string contains " - ", split it and take only the part before " - "
  // This removes the task information while preserving honorific and name
  if (typeof proponentsString === 'string' && proponentsString.includes(' - ')) {
    return proponentsString.split(' - ')[0].trim();
  }
  
  return proponentsString.trim();
};

const MonitoringView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [monitoring, setMonitoring] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDecision, setShowDecision] = useState(false);
  const [decision, setDecision] = useState('Approved');
  const [remarks, setRemarks] = useState('');
  const { notification, showSuccess, showError, dismiss } = useNotification();
  const [editLockLoading, setEditLockLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [generatingDocx, setGeneratingDocx] = useState(false);

  // Toggle edit lock function
  const handleToggleEditLock = async () => {
    if (!monitoring) return;
    setEditLockLoading(true);
    try {
      const newLockState = !monitoring.edit_locked;
      await apiClient.put(`/monitoring/${monitoring.monitoring_id}/edit-lock`, { edit_locked: newLockState });
      setMonitoring(prev => ({ ...prev, edit_locked: newLockState }));
      setSuccessMessage(`Edit permissions ${newLockState ? 'locked' : 'unlocked'} successfully.`);
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Failed to toggle edit lock:', err);
      showError('Failed to toggle edit lock');
    } finally {
      setEditLockLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await apiClient.get(`/monitoring/${id}`);
        setMonitoring(data);
      } catch (e) {
        console.error('Error loading monitoring tool:', e);
        showError('Failed to load monitoring tool');
        navigate('/monitoring');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate, showError]);

  const statusVariant = (status) => {
    switch (status) {
      case 'Approved by Extension Head':
      case 'Approved by VC-RDES':
        return 'success';
      case 'Rejected by Extension Head':
      case 'Rejected by VC-RDES':
        return 'danger';
      case 'Pending':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  // Get approval checklist for monitoring tools
  const getApprovalChecklist = (monitoring) => {
    const s = monitoring.status;
    
    // Only mark as done if approved, not if rejected
    const ehDone = ['Approved by Extension Head', 'Approved by VC-RDES'].includes(s);
    const vcrdesDone = ['Approved by VC-RDES'].includes(s);
    
    // Check if rejected
    const ehRejected = s === 'Rejected by Extension Head';
    const vcrdesRejected = s === 'Rejected by VC-RDES';

    const steps = [
      {
        key: 'eh',
        label: 'Approved by Extension Head',
        done: ehDone,
        rejected: ehRejected
      },
      {
        key: 'vcrdes',
        label: 'Approved by VC-RDES',
        done: vcrdesDone,
        rejected: vcrdesRejected
      }
    ];

    return steps;
  };

  // Get next approval step
  const getNextApprovalStep = (monitoring) => {
    const s = monitoring.status;
    if (['Rejected by Extension Head', 'Rejected by VC-RDES'].includes(s)) return null;
    if (s === 'Pending') return 'To be reviewed by Extension Head';
    if (s === 'Approved by Extension Head') return 'To be approved by VC-RDES';
    if (s === 'Approved by VC-RDES') return null;
    return null;
  };

  // Check if current user can make approval decisions
  const canCurrentUserDecide = (monitoring) => {
    if (!user) return false;
    const role = user.role;
    const s = monitoring.status;
    if (role === 'Extension Head') return s === 'Pending';
    if (role === 'Vice Chancellor for Research, Development and Extension Services') return s === 'Approved by Extension Head';
    if (role === 'Admin') return ['Pending', 'Approved by Extension Head'].includes(s);
    return false;
  };

  // Handle decision button clicks
  const handleDecisionClick = (decisionType) => {
    setDecision(decisionType);
    setRemarks('');
    setShowDecision(true);
  };

  // Submit approval decision
  const submitDecision = async () => {
    try {
      setLoading(true);
      const response = await apiClient.put(`/monitoring/${id}/status`, {
        decision: decision,
        remarks: remarks || null
      });
      
      if (response.data) {
        // Reload the monitoring tool data
        const { data } = await apiClient.get(`/monitoring/${id}`);
        setMonitoring(data);
        showSuccess(`Monitoring tool ${decision.toLowerCase()} successfully!`);
        setShowDecision(false);
        setRemarks('');
      }
    } catch (err) {
      console.error('Error submitting decision:', err);
      showError('Failed to submit decision. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTemplate = async () => {
    if (!monitoring) {
      showError('Monitoring tool data not loaded. Please refresh the page.');
      return;
    }

    setGeneratingDocx(true);
    try {
      const response = await apiClient.post(
        `/monitoring/generate-docx`,
        { monitoring_id: id },
        { 
          responseType: 'blob',
          validateStatus: (status) => status < 500 // Don't throw on 4xx, we'll handle it
        }
      );

      // Check if response is actually an error (JSON) or a blob (DOCX)
      if (response.headers['content-type']?.includes('application/json')) {
        // Error response - parse the blob as JSON
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        showError(errorData.message || errorData.error || 'Failed to generate document');
        return;
      }

      // Success - it's a DOCX file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `Monitoring_Tool_${monitoring?.extension_program_title?.replace(/[^a-zA-Z0-9]/g, '_') || 'Project'}_${Date.now()}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('Monitoring Tool document generated and downloaded successfully!');
    } catch (error) {
      console.error('Error generating DOCX document:', error);
      console.error('Error response:', error.response);
      
      // Try to parse error response if it's a blob
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          showError(errorData.message || errorData.error || 'Failed to generate document');
        } catch (parseError) {
          showError('Failed to generate document. Please check the server logs.');
        }
      } else if (error.response?.data?.message) {
        showError(error.response.data.message);
      } else if (error.response?.data?.error) {
        showError(error.response.data.error);
      } else {
        showError('Failed to generate document. Please try again.');
      }
    } finally {
      setGeneratingDocx(false);
    }
  };

  const formatNumberWithCommas = (value) => {
    if (!value) return '';
    
    const stringValue = String(value);
    const cleanValue = stringValue.replace(/[^\d.]/g, '');
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    if (decimalPart !== undefined) {
      const limitedDecimal = decimalPart.substring(0, 2);
      return formattedInteger + '.' + limitedDecimal;
    }
    
    return formattedInteger;
  };

  const renderWorkPlanTable = (workPlan) => {
    if (!workPlan) return null;
    
    let rows = [];
    try {
      const parsed = typeof workPlan === 'string' ? JSON.parse(workPlan) : workPlan;
      if (parsed && Array.isArray(parsed)) rows = parsed;
    } catch (e) {
      console.error('Error parsing work plan:', e);
    }

    if (rows.length === 0) return <div className="text-muted">No work plan data available</div>;

    return (
      <div className="table-responsive">
        <Table bordered hover className="table-sm">
          <thead className="table-primary">
            <tr>
              <th style={{ width: '15%' }}>Activity</th>
              <th style={{ width: '10%' }}>Physical Target</th>
              <th style={{ width: '10%' }}>Actual Accomplishment</th>
              <th style={{ width: '10%' }}>% Accomplished</th>
              <th style={{ width: '15%' }}>Problems/Challenges</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td style={{ whiteSpace: 'pre-wrap' }}>{row.activities || '—'}</td>
                <td>{row.physicalTargets || '—'}</td>
                <td>{row.actualAccomplishment || '—'}</td>
                <td>{row.percentageAccomplished || '—'}</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>{row.problemsChallenges || '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderBudgetUtilizationTable = (budgetUtilization, budgetTotals) => {
    // Default budget rows structure
    const defaultBudgetRows = [
      {
        id: 1,
        type: 'Purchase Request',
        details: '',
        amountRequested: '',
        actualAmount: '',
        budgetUtilized: '',
        remarks: ''
      },
      {
        id: 2,
        type: 'Cash Advance',
        details: '',
        amountRequested: '',
        actualAmount: '',
        budgetUtilized: '',
        remarks: ''
      },
      {
        id: 3,
        type: 'Request of Payment',
        details: '',
        amountRequested: '',
        actualAmount: '',
        budgetUtilized: '',
        remarks: ''
      }
    ];
    
    let rows = [];
    try {
      const parsed = typeof budgetUtilization === 'string' ? JSON.parse(budgetUtilization) : budgetUtilization;
      if (parsed && Array.isArray(parsed)) {
        // Merge existing data with default structure
        rows = defaultBudgetRows.map(defaultRow => {
          const existingRow = parsed.find(row => row.type === defaultRow.type);
          return existingRow ? { ...defaultRow, ...existingRow } : defaultRow;
        });
      } else {
        rows = defaultBudgetRows;
      }
    } catch (e) {
      console.error('Error parsing budget utilization:', e);
      rows = defaultBudgetRows;
    }

    if (rows.length === 0) return <div className="text-muted">No budget utilization data available</div>;

    // Parse budget totals
    let totals = {};
    try {
      totals = budgetTotals ? (typeof budgetTotals === 'string' ? JSON.parse(budgetTotals) : budgetTotals) : {};
      console.log('Budget totals parsed:', totals);
    } catch (e) {
      console.error('Error parsing budget totals:', e);
      totals = {};
    }

    // If manual totals are not available, calculate from rows as fallback
    if (!totals.totalAmountRequested && !totals.totalActualAmount && !totals.totalBudgetUtilized) {
      const calculatedTotals = rows.reduce((acc, row) => {
        acc.totalAmountRequested += parseFloat(row.amountRequested || 0);
        acc.totalActualAmount += parseFloat(row.actualAmount || 0);
        acc.totalBudgetUtilized += parseFloat(row.budgetUtilized || 0);
        return acc;
      }, { totalAmountRequested: 0, totalActualAmount: 0, totalBudgetUtilized: 0 });
      
      // Only use calculated totals if they have values
      if (calculatedTotals.totalAmountRequested > 0 || calculatedTotals.totalActualAmount > 0 || calculatedTotals.totalBudgetUtilized > 0) {
        totals = calculatedTotals;
        console.log('Using calculated totals as fallback:', totals);
      }
    }

    return (
      <div className="table-responsive">
        <Table bordered hover className="table-sm">
          <thead className="table-primary">
            <tr>
              <th style={{ width: '20%' }}>Type of Request</th>
              <th style={{ width: '15%' }}>Amount Requested</th>
              <th style={{ width: '15%' }}>Actual Amount</th>
              <th style={{ width: '10%' }}>Budget Utilized</th>
              <th style={{ width: '25%' }}>Remarks/Challenges</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>{row.type || '—'}</td>
                <td style={{ textAlign: 'center' }}>{row.amountRequested ? formatNumberWithCommas(row.amountRequested) : '—'}</td>
                <td style={{ textAlign: 'center' }}>{row.actualAmount ? formatNumberWithCommas(row.actualAmount) : '—'}</td>
                <td style={{ textAlign: 'center' }}>{row.budgetUtilized || '—'}</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>{row.remarks || '—'}</td>
              </tr>
            ))}
            {/* Total row */}
            <tr className="table-light">
              <td><strong>Total</strong></td>
              <td style={{ textAlign: 'center' }}><strong>{totals.totalAmountRequested ? formatNumberWithCommas(totals.totalAmountRequested) : '—'}</strong></td>
              <td style={{ textAlign: 'center' }}><strong>{totals.totalActualAmount ? formatNumberWithCommas(totals.totalActualAmount) : '—'}</strong></td>
              <td style={{ textAlign: 'center' }}><strong>{totals.totalBudgetUtilized || '—'}</strong></td>
              <td></td>
            </tr>
          </tbody>
        </Table>
      </div>
    );
  };

  if (loading) return <div className="container-fluid px-3 px-md-4 py-3">Loading...</div>;
  if (!monitoring) return <div className="container-fluid px-3 px-md-4 py-3">Monitoring tool not found</div>;

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      <div className="d-flex justify-content-end align-items-center mb-3" style={{ marginBottom: '1rem' }}>
        <div className="d-flex gap-2">
          {/* Approve/Reject buttons - based on role and status in workflow */}
          {canCurrentUserDecide(monitoring) && (
            <>
              <Button 
                size="sm" 
                variant="success" 
                style={{ minWidth: '40px', height: '32px' }} 
                onClick={() => handleDecisionClick('Approved')}
                disabled={loading}
                title="Approve"
              >
                <i className="bi bi-check-lg"></i>
              </Button>
              <Button 
                size="sm" 
                variant="danger" 
                style={{ minWidth: '40px', height: '32px' }} 
                onClick={() => handleDecisionClick('Rejected')}
                disabled={loading}
                title="Reject"
              >
                <i className="bi bi-x-lg"></i>
              </Button>
            </>
          )}

          {/* Edit button - using new permission system */}
          {user && monitoring && (() => {
            const permissions = getItemPermissions(user, monitoring, 'monitoring');
            return permissions.canEdit;
          })() && (
            <Button 
              onClick={() => setShowEditModal(true)}
              variant="outline-primary" 
              size="sm" 
              style={{ minWidth: '40px', height: '32px' }}
              title="Edit"
            >
              <i className="bi bi-pencil"></i>
            </Button>
          )}

          {/* Lock/Unlock Edit Permission button - for elevated roles */}
          {user && monitoring && canToggleEditLock(user) && (
            <Button 
              variant={monitoring.edit_locked ? "warning" : "outline-secondary"}
              size="sm"
              style={{ minWidth: '40px', height: '32px' }}
              onClick={handleToggleEditLock}
              disabled={editLockLoading}
              title={monitoring.edit_locked ? "Unlock Edit Permission" : "Lock Edit Permission"}
            >
              <i className={`bi ${monitoring.edit_locked ? 'bi-lock-fill' : 'bi-unlock'}`}></i>
            </Button>
          )}

          {/* Delete button - using new permission system */}
          {user && monitoring && (() => {
            const permissions = getItemPermissions(user, monitoring, 'monitoring');
            return permissions.canDelete;
          })() && (
            <Button 
              variant="outline-danger" 
              size="sm"
              style={{ minWidth: '40px', height: '32px' }}
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this monitoring tool?')) {
                  // Handle delete logic here
                  console.log('Delete monitoring tool:', monitoring.monitoring_id);
                }
              }}
              title="Delete Monitoring Tool"
            >
              <i className="bi bi-trash"></i>
            </Button>
          )}

          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={handleGenerateTemplate}
            disabled={generatingDocx || !monitoring}
            title="Generate and download Monitoring Tool document (DOCX)"
            style={{
              borderRadius: '8px',
              borderWidth: '1.5px',
              backgroundColor: 'white',
              borderColor: '#0d6efd',
              color: '#0d6efd',
              minWidth: '40px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {generatingDocx ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '1rem', height: '1rem', borderWidth: '0.15em', borderColor: '#0d6efd', borderRightColor: 'transparent' }}></span>
            ) : (
              <i className="bi bi-file-earmark-text" style={{ fontSize: '1.1rem' }}></i>
            )}
          </Button>

          <Button 
            as={Link} 
            to="/monitoring" 
            variant="secondary" 
            size="sm" 
            style={{ minWidth: '40px', height: '32px' }}
            title="Back"
          >
            <i className="bi bi-arrow-left"></i>
          </Button>
        </div>
      </div>

      <Card className="shadow-sm">
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          
          {/* Monitoring Tool Title */}
          <div className="mb-4">
            <h4 
              className="text-dark mb-2" 
              style={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                maxWidth: '100%'
              }}
              title={monitoring.project_title}
            >
              {monitoring.project_title}
            </h4>
          </div>
          
          <Row className="mb-3">
            <Col md={6}>
              <div><strong>Proponents:</strong> {formatProponents(monitoring.proponents) || '—'}</div>
              <div className="mt-1"><strong>Created by:</strong> {monitoring.creator_name || '—'}</div>
            </Col>
            <Col md={6}>
              <strong>Status:</strong> <Badge bg={statusVariant(monitoring.status)}>{monitoring.status}</Badge>
              <div className="mt-1"><strong>Date:</strong> {new Date(monitoring.date).toLocaleDateString()}</div>
              <div className="mt-1"><strong>Created:</strong> {new Date(monitoring.created_at).toLocaleString()}</div>
              
              
              {/* Approval Workflow under Status - following Project View format */}
              <div className="mt-2">
                {(() => {
                  const next = getNextApprovalStep(monitoring);
                  return next ? (
                    <div className="text-muted small mt-1 d-flex align-items-center" style={{ fontStyle: 'italic' }}>
                      <i className="bi bi-eye me-1" aria-hidden="true"></i>
                      <span>{next}</span>
                    </div>
                  ) : null;
                })()}
                
                {/* Approval Checklist */}
                <div className="mt-2">
                  {getApprovalChecklist(monitoring).map(step => (
                    <div key={step.key} className="d-flex align-items-center small">
                      <i className={`bi ${step.done ? 'bi-check-square-fill text-success' : 'bi-square text-muted' } me-1`} aria-hidden="true"></i>
                      <span className="text-muted">{step.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          </Row>

          {/* Remarks for approved or rejected status */}
          {monitoring.remarks && (
            <Row className="mb-3">
              <Col md={12}>
                {(['Rejected by Extension Head','Rejected by VC-RDES'].includes(monitoring.status)) ? (
                  <div className="alert alert-info border-0" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #dc3545' }}>
                    <div className="d-flex align-items-start">
                      <i className="bi bi-exclamation-triangle-fill text-danger me-2 mt-1" style={{ fontSize: '1.2rem' }}></i>
                      <div className="w-100">
                        <strong className="text-danger d-block">
                          <i className="bi bi-exclamation-circle-fill me-1"></i>Remarks:
                        </strong>
                        <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{monitoring.remarks}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-info border-0" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #28a745' }}>
                    <div className="d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-success me-2 mt-1" style={{ fontSize: '1.2rem' }}></i>
                      <div className="w-100">
                        <strong className="text-success d-block">
                          <i className="bi bi-info-circle-fill me-1"></i>Remarks:
                        </strong>
                        <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{monitoring.remarks}</div>
                      </div>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          )}

          <Row className="mb-3">
            <Col md={6}>
              <div><strong>Total Project Cost:</strong> ₱{formatNumberWithCommas(monitoring.total_project_cost)}</div>
            </Col>
            <Col md={6}>
              <div><strong>Duration:</strong> {monitoring.duration || '—'}</div>
            </Col>
          </Row>

          <hr />

          {/* Section A: Work Plan */}
          <div className="mb-4">
            <h5 className="mb-3">Section A. Work Plan</h5>
            {renderWorkPlanTable(monitoring.work_plan)}
          </div>

          <hr />

          {/* Section B: Budget Utilization */}
          <div className="mb-4">
            <h5 className="mb-3">Section B. Budget Utilization</h5>
            {renderBudgetUtilizationTable(monitoring.budget_utilization, monitoring.budget_totals)}
          </div>

        </Card.Body>
      </Card>

      {/* Approve/Reject Modal */}
      <Modal show={showDecision} onHide={() => setShowDecision(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{decision === 'Approved' ? 'Approve Monitoring Tool' : 'Reject Monitoring Tool'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {decision === 'Approved' && (
            <>
              <div className="text-center py-3">
                <i className="bi bi-question-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3 mb-2">Confirm Monitoring Tool Approval</h5>
                <p className="text-muted">Are you sure you want to approve this monitoring tool?</p>
              </div>
              <Form.Group>
                <Form.Label>Remarks (optional)</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3} 
                  value={remarks} 
                  onChange={(e) => setRemarks(e.target.value)} 
                  placeholder="Add any additional comments or notes..."
                />
              </Form.Group>
            </>
          )}
          {decision === 'Rejected' && (
            <>
              <div className="text-center py-3">
                <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3 mb-2">Confirm Monitoring Tool Rejection</h5>
                <p className="text-muted">Are you sure you want to reject this monitoring tool?</p>
              </div>
              <Form.Group>
                <Form.Label>Remarks (optional)</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3} 
                  value={remarks} 
                  onChange={(e) => setRemarks(e.target.value)} 
                  placeholder="Please provide a reason for rejection..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDecision(false)}>Cancel</Button>
          <Button 
            variant={decision === 'Approved' ? 'success' : 'danger'} 
            onClick={submitDecision}
          >
            {decision === 'Approved' ? 'Approve' : 'Reject'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <h5 className="mb-0">{successMessage}</h5>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => setShowSuccessModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Monitoring Tool Modal */}
      {monitoring && (
        <MonitoringToolModal
          show={showEditModal}
          onHide={() => {
            setShowEditModal(false);
            // Reload monitoring data after edit
            const load = async () => {
              try {
                const { data } = await apiClient.get(`/monitoring/${id}`);
                setMonitoring(data);
              } catch (e) {
                console.error('Error loading monitoring tool:', e);
              }
            };
            load();
          }}
          monitoringId={monitoring.monitoring_id}
          onSuccess={() => {
            setShowEditModal(false);
            // Reload monitoring data after successful edit
            const load = async () => {
              try {
                const { data } = await apiClient.get(`/monitoring/${id}`);
                setMonitoring(data);
              } catch (e) {
                console.error('Error loading monitoring tool:', e);
              }
            };
            load();
          }}
        />
      )}
    </div>
  );
};

export default MonitoringView;
